import React, { ChangeEvent, useState } from 'react';

import { Box } from '@rbilabs/component-library';
import { defineMessages, useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { IDeliveryAddress } from 'generated/rbi-graphql';
import useMediaQuery from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

import { StyledModalContent, StyledModalHeader } from '../styled';

import DeliveryAddressItem from './delivery-address-item';
import { AddressInstructions, AddressList } from './styled';
import { IDeliveryAddressesProps } from './types';

const messages = defineMessages({
  deliverySelectYourAddress: {
    id: 'deliverySelectYourAddress',
    defaultMessage: 'Select your Delivery Address',
    description: 'Text asking the user to select their delivery address',
  },
  deliverHere: {
    id: 'deliverHere',
    defaultMessage: 'Deliver Here',
    description: 'Button text for selecting the delivery address',
  },
  deliveryAddNewAddress: {
    id: 'deliveryAddNewAddress',
    defaultMessage: 'Add new address',
    description: 'Button text to take the user to enter a new address',
  },
  deliveryInstructions: {
    id: 'deliveryInstructions',
    defaultMessage: 'Delivery Instructions',
    description: 'Text label for delivery instructions',
  },
});

const DeliveryAddresses: React.FC<IDeliveryAddressesProps> = ({
  addresses,
  onCreate,
  setAddress,
}) => {
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState(-1);
  const [instructions, setInstructions] = useState('');
  const { logEvent } = useMParticleContext();
  const enableSimplifiedDelivery = useFlag(LaunchDarklyFlag.ENABLE_SIMPLE_DELIVERY_FORM);
  const isDesktop = useMediaQuery('desktop');

  const handleInstructionsChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInstructions(e.target.value);
  };

  const onSelectAddress = async () => {
    const address = addresses[selected];
    logEvent(CustomEventNames.SELECT_DELIVERY_SAVED_ADDRESS, EventTypes.Other);

    const selectedAddressComponents = {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || undefined,
      city: address.city,
      state: address.state,
      zip: address.zip,
      country: address.country,
    };

    const selectedPhoneNumber = address.phoneNumber;
    setAddress({
      address: selectedAddressComponents,
      phoneNumber: selectedPhoneNumber,
      deliveryInstructions: instructions,
    });
  };

  return (
    <>
      <StyledModalContent data-testid="delivery-addresses">
        <StyledModalHeader>{formatMessage(messages.deliverySelectYourAddress)}</StyledModalHeader>
      </StyledModalContent>
      <AddressList>
        {addresses?.map((address: IDeliveryAddress, index: number) => (
          <DeliveryAddressItem
            key={index}
            address={address}
            selected={index === selected}
            onSelect={() => setSelected(index)}
          />
        ))}
      </AddressList>
      <>
        {!enableSimplifiedDelivery && (
          <AddressInstructions
            label={formatMessage(messages.deliveryInstructions)}
            name="deliveryInstructions"
            type="text"
            maxLength={512}
            value={instructions}
            onChange={handleInstructionsChange}
          />
        )}
        <Box flexDirection="column" center padding="0 1rem">
          <Box width={isDesktop ? '50%' : '100%'} center margin="0 0 1rem">
            <ActionButton
              disabled={selected < 0}
              data-testid="select-delivery-address"
              onClick={onSelectAddress}
              fullWidth
              variant={ActionButtonVariants.PRIMARY}
            >
              {formatMessage(messages.deliverHere)}
            </ActionButton>
          </Box>
          <Box width={isDesktop ? '50%' : '100%'} center margin="0 0 1rem">
            <ActionButton
              data-testid="create-delivery-address"
              onClick={() => onCreate()}
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
            >
              {formatMessage(messages.deliveryAddNewAddress)}
            </ActionButton>
          </Box>
        </Box>
      </>
    </>
  );
};

export default DeliveryAddresses;
